import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import services from '../services';
import withResponseHandling from './withResponseHandling';
import withMessage from './withMessage';
import { acCarteirasChanged } from '../store/actions';
import { AVALIACAO_SESSOES_QUESTOES } from '../enums';

const PAGE_SIZE = 25;
const FALLBACK_QUESTOES = Object.values(AVALIACAO_SESSOES_QUESTOES).flatMap(xSessao =>
  xSessao.questoes.map(xQuestao => ({
    ...xQuestao,
    modelos: xQuestao.modelos || [],
    sessao_label: xQuestao.sessao_label || xSessao.label || xSessao.sessao || ''
  }))
);

const withAvaliacoesQuestoes = Component => {
  function WrapComponent(props) {
    const entidadeId = useSelector(state => state.entidade.current.default_entidade_id);
    const storeDispatch = useDispatch();

    const [avaliacoes, setAvaliacoes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefetching, setIsRefetching] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [rowSelection, setRowSelection] = useState({});
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: PAGE_SIZE
    });

    function updateStore(pData) {
      storeDispatch(acCarteirasChanged(pData));
    }

    const readAvaliacoes = useCallback(
      async (pParams = {}, pCallback) => {
        if (!avaliacoes.length) {
          setIsLoading(true);
        } else {
          setIsRefetching(true);
        }

        const xParams = {
          size: PAGE_SIZE,
          page: 1,
          sort: 'favorito desc, carteira',
          ...pParams
        };

        try {
          const rRes = await services.mockAvaliacoes.questoesRepo.list();

          if (!rRes.ok) {
            props.responseErrorHandling(rRes.error);
            return;
          }

          const xData = Array.isArray(rRes.data) ? rRes.data : [];
          const xAllItems = xData.length ? xData : FALLBACK_QUESTOES;
          const xPage = Number(xParams.page) > 0 ? Number(xParams.page) : 1;
          const xSize = Number(xParams.size) > 0 ? Number(xParams.size) : PAGE_SIZE;
          const xStart = (xPage - 1) * xSize;
          const xItems = xAllItems.slice(xStart, xStart + xSize);

          setAvaliacoes(xItems);
          setRowCount(xAllItems.length);
          pCallback && pCallback(xItems);
        } catch (error) {
          props.responseErrorHandling(error);
        } finally {
          setIsLoading(false);
          setIsRefetching(false);
        }
      },
      [avaliacoes.length, props]
    );

    const deleteAvaliacoes = async (pId, onFinish) => {
      try {
        const rRes = await services.mockAvaliacoes.questoesRepo.remove(pId);

        if (!rRes.ok) {
          props.responseErrorHandling(rRes.error);
          return;
        }

        props.onMessageSuccess('Questão excluída com sucesso');
        readAvaliacoes({}, updateStore);
        setRowSelection({});
      } catch (error) {
        props.responseErrorHandling(error);
      } finally {
        onFinish && onFinish();
      }
    };

    const bulkDeleteAvaliacoes = pRows => {
      const xAvaliacoesId = pRows.map(xRow => xRow.original.id).filter(Boolean);

      xAvaliacoesId.forEach(xId => {
        deleteAvaliacoes(xId);
      });
    };

    useEffect(() => {
      const xPage = pagination.pageIndex + 1;

      readAvaliacoes({
        page: xPage,
        size: pagination.pageSize
      });
    }, [readAvaliacoes, pagination.pageIndex, pagination.pageSize, entidadeId]);

    const xProps = {
      avaliacoes,
      isLoading,
      isRefetching,
      rowCount,
      rowSelection,
      pagination,
      setPagination,
      setRowSelection,
      readAvaliacoes,
      deleteAvaliacoes,
      bulkDeleteAvaliacoes
    };

    return <Component {...xProps} />;
  }

  return withMessage(withResponseHandling(WrapComponent));
};

export default withAvaliacoesQuestoes;
